<template>
  <div class="inform-settings pa-2 w-100">
    <Typography variant="subtitle-1" class="mb-2">Inform Tenant</Typography>
    <Typography variant="body-2" class="mb-4">
      Save the Inform tenantId for this client.
    </Typography>

    <v-text-field
      v-model="informTenantId"
      label="Inform Tenant Id"
      outlined
      dense
      hide-details="auto"
      class="mb-4"
    />

    <Button
      color="brandPrimary"
      :loading="saving"
      :disabled="!hasClientId || (!informTenantIdTrimmed && !hasSavedTenantId) || refreshing"
      @click="handleSaveTenantId"
    >
      Save Changes
    </Button>

    <!--
      Everything below branches on `engageUserSyncEnabled`, and the flag defaults to
      false, so rendering before it resolves shows the OFF layout and then swaps it —
      which reads as the save button being missing. Held until the set in the store was
      evaluated for THIS client; see `isClientFeatureFlagsResolvedFor`.
    -->
    <div v-if="hasSavedTenantId && !clientFeatureFlagsReady" class="d-flex justify-center py-8">
      <v-progress-circular indeterminate color="brandPrimary" />
    </div>

    <template v-if="hasSavedTenantId && clientFeatureFlagsReady">
      <v-divider class="my-6" />

      <Typography variant="subtitle-1" class="mb-2">Schema Refresh</Typography>
      <Typography variant="body-2" class="mb-4">
        Trigger a schema refresh within Inform for this tenant.
      </Typography>

      <Button
        color="brandPrimary"
        :loading="refreshing"
        :disabled="refreshDisabled"
        @click="handleRefreshSchemas"
      >
        Refresh Schemas
      </Button>

      <v-divider class="my-6" />

      <Typography variant="subtitle-1" class="mb-2">Schema Mapping</Typography>
      <div class="mb-4">
        <Typography variant="body-2" as="p" class="mb-0">
          Map your Inform schema attributes to Engage user properties.
        </Typography>
        <Typography variant="body-2" as="p" class="mb-0">
          Required fields (*) must be mapped before saving.
        </Typography>
      </div>

      <v-select
        v-model="schemaField.value"
        :items="schemaOptions"
        label="Schema"
        outlined
        dense
        hide-details="auto"
        color="brandPrimary"
        :loading="schemaField.loading || schemaField.saving"
        :disabled="!hasClientId || schemaField.loading || schemaField.saving || refreshing"
        class="mb-4"
      />

      <div class="schema-mapping-table">
        <div class="schema-mapping-header d-flex align-center">
          <Typography variant="subtitle-2" class="schema-mapping-col-engage">
            Engage User Property
          </Typography>
          <Typography variant="subtitle-2" class="schema-mapping-col-inform">
            Inform Attribute
          </Typography>
        </div>
        <v-divider class="schema-mapping-grid-line" />

        <div v-for="row in schemaMappingRows" :key="row.id" class="schema-mapping-block">
          <div
            class="schema-mapping-row d-flex align-center flex-wrap"
            :class="
              row.required && mockSchemaShowValidation && !row.informAttribute
                ? 'pt-1 pb-0'
                : 'py-2 py-sm-1'
            "
          >
            <div class="d-flex align-center flex-wrap py-1 schema-mapping-col-engage">
              <Typography variant="body-2" as="span" class="mr-2">
                {{ row.engageLabel }}<span v-if="row.required">*</span>
              </Typography>
              <StatusChip
                outlined
                small
                :text="row.dataTypeLabel"
                :variant="dataTypeToStatusVariant(row.dataType)"
              />
            </div>
            <div class="schema-mapping-col-inform">
              <v-select
                v-model="row.informAttribute"
                :items="informAttributeOptions"
                :loading="schemaField.loading || schemaField.saving"
                :disabled="!hasClientId || schemaField.loading || schemaField.saving || refreshing"
                placeholder="Select"
                outlined
                dense
                clearable
                color="brandPrimary"
                :error="isMockMappingRowInError(row)"
                :error-messages="mockMappingErrorMessages(row)"
                class="mt-4"
                @change="(newValue) => handleInformAttributeChange(row, newValue)"
              >
                <template #item="{ item }">
                  <span
                    :class="{
                      'v-list-item--disabled theme--light': isAttributeUsedElsewhere(item, row),
                    }"
                  >
                    {{ item }}
                  </span>
                </template>
              </v-select>
            </div>
          </div>
          <v-divider class="schema-mapping-grid-line" />
        </div>
      </div>

      <template v-if="engageUserSyncEnabled">
        <v-divider class="my-6" />

        <Typography variant="subtitle-1" class="mb-2">Engage Override Fields</Typography>
        <div class="mb-4">
          <Typography variant="body-2" as="p" class="mb-0">
            Fields an edit made in Engage is allowed to keep when the customer feed carries a
            different value. Every field left unselected lets the feed win.
          </Typography>
        </div>

        <v-select
          v-model="allowOverrideProperties"
          :items="overrideOptions"
          label="Engage Override Fields"
          multiple
          chips
          deletable-chips
          outlined
          dense
          hide-details="auto"
          color="brandPrimary"
          :disabled="!hasClientId || savingConfiguration || refreshing"
          class="mb-4 override-select"
        />
      </template>

      <v-divider class="my-6" />

      <Typography variant="subtitle-1" class="mb-2">
        {{ engageUserSyncEnabled ? "User Sync Filter" : "GraphQL User Sync Filter" }}
      </Typography>
      <div class="mb-4">
        <template v-if="engageUserSyncEnabled">
          <Typography variant="body-2" as="p" class="mb-0">
            A JSON array of predicates, all of which must hold. Name each property by its Engage
            User Property name, not its Inform attribute. An Inform attribute you have not mapped
            keeps its own name. Operators:
            <code>eq</code>, <code>ne</code>, <code>in</code>, <code>notIn</code>, <code>gt</code>,
            <code>gte</code>, <code>lt</code>, <code>lte</code>, <code>exists</code>,
            <code>notExists</code>.
          </Typography>
          <pre class="inform-filter-example">{{ informFilterExample }}</pre>
        </template>
        <template v-else>
          <Typography variant="body-2" as="p" class="mb-0">
            Neo4j GraphQL filter to scope which users are included (e.g.,
            <code>{ role: { eq: "admin" } }</code>).
          </Typography>
          <a
            href="https://neo4j.com/docs/graphql/current/queries-aggregations/filtering/"
            target="_blank"
            rel="noopener noreferrer"
            class="inform-neo4j-link"
          >
            Neo4j documentation
          </a>
        </template>
      </div>

      <v-textarea
        v-model="syncFilterField.value"
        :label="engageUserSyncEnabled ? 'User Sync Filter*' : 'GraphQL Valid User Sync Filter*'"
        outlined
        hide-details="auto"
        :loading="syncFilterField.loading || syncFilterField.saving"
        :disabled="!hasClientId || refreshing"
        :error-messages="syncFilterError ? [syncFilterError] : []"
        class="graphql-filter-field mb-4"
      />

      <v-textarea
        v-model="cleanupFilterField.value"
        :label="
          engageUserSyncEnabled
            ? 'User Cleanup Filter (optional)'
            : 'GraphQL User Cleanup Filter (optional)'
        "
        outlined
        hide-details="auto"
        :loading="cleanupFilterField.loading || cleanupFilterField.saving"
        :disabled="!hasClientId || refreshing"
        :error-messages="cleanupFilterError ? [cleanupFilterError] : []"
        class="graphql-filter-field"
      />

      <template v-if="engageUserSyncEnabled">
        <v-divider class="my-6" />

        <Typography variant="body-2" as="p" class="mb-4">
          Saving writes these settings and updates how this client's users are merged in the graph.
          Leave the schema blank to declare that Engage owns this client's users.
        </Typography>

        <Button
          color="brandPrimary"
          :loading="savingConfiguration"
          :disabled="saveConfigurationDisabled"
          @click="handleSaveInformConfiguration"
        >
          Save Inform Configuration
        </Button>
      </template>
    </template>

    <ErrorDialogV2
      v-model="confirmDialog.show"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :confirm-button-text="confirmDialog.confirmButtonText"
      :cancel-button-text="confirmDialog.cancelButtonText"
      :show-cancel-button="true"
      @confirm="onConfirmDialogConfirm"
      @cancel="onConfirmDialogCancel"
    />
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import StatusChip from "@/components/generic/StatusChip.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";
import ClientServiceV2 from "@/services/ClientServiceV2";
import InformService from "@/services/InformService";
import clientSupplementalMixin from "@/mixins/clientSupplementalMixin";
import clientFeatureFlagsMixin from "@/mixins/clientFeatureFlags";
import { debounce } from "@/shared_data/functions";
import SupplementalField from "./SupplementalField";
import { INFORM_FILTER_EXAMPLE, validateInformFilter } from "./informFilter";
import { buildEngageConfiguration, engageConfigurationMatches } from "./informConfiguration";

const INFORM_USER_SCHEMA_KEY = "inform_user_schema";
const INFORM_USER_MAP_KEY = "inform_user_map";
const INFORM_USER_SYNC_FILTER_KEY = "inform_user_sync_where";
const INFORM_USER_CLEANUP_FILTER_KEY = "inform_user_cleanup_where";

const FILTER_DEBOUNCE_MS = 1000;

export default {
  name: "InformSettings",
  components: {
    Typography,
    Button,
    StatusChip,
    ErrorDialogV2,
  },
  mixins: [clientSupplementalMixin, clientFeatureFlagsMixin],
  props: {
    client: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      clientService: new ClientServiceV2(),
      informService: new InformService(),
      informTenantId: "",
      saving: false,
      refreshing: false,
      // Engage user sync (ADO 186742). Only reachable while the client-scoped flag is
      // on for the selected client; with it off none of this is read or rendered.
      savingConfiguration: false,
      allowOverrideProperties: [],
      informFilterExample: INFORM_FILTER_EXAMPLE,
      // How long the save waits for its own write to appear. The POST is accepted (202)
      // and applied asynchronously off a Kafka topic, so the document does not change the
      // instant the call returns. Data rather than constants so a test can collapse the
      // wait without faking timers.
      configurationConfirmAttempts: 5,
      configurationConfirmDelayMs: 1000,
      schemaField: new SupplementalField({
        key: INFORM_USER_SCHEMA_KEY,
        label: "Schema",
      }),
      schemas: [],
      mapField: new SupplementalField({
        key: INFORM_USER_MAP_KEY,
        label: "Inform user map",
        defaultValue: "{}",
      }),
      syncFilterField: new SupplementalField({
        key: INFORM_USER_SYNC_FILTER_KEY,
        label: "User sync filter",
        softDeleteOnEmpty: true,
      }),
      cleanupFilterField: new SupplementalField({
        key: INFORM_USER_CLEANUP_FILTER_KEY,
        label: "User cleanup filter",
        softDeleteOnEmpty: true,
      }),
      // When true, the `selectedSchema` watcher skips its change handler. Used
      // to revert the v-select to its previous value after the user cancels
      // the schema-change confirmation dialog.
      isRevertingSelectedSchema: false,
      // Single-instance ErrorDialogV2 driven by `confirm()` below.
      confirmDialog: {
        show: false,
        title: "",
        message: "",
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
      },
      confirmDialogResolve: null,
      /** Mock UI state — wire to API later */
      mockSchemaShowValidation: true,
      schemaMappingRows: [
        {
          id: "firstName",
          engageLabel: "First Name",
          required: true,
          dataType: "string",
          dataTypeLabel: "string",
          informAttribute: null,
        },
        {
          id: "lastName",
          engageLabel: "Last Name",
          required: true,
          dataType: "string",
          dataTypeLabel: "string",
          informAttribute: null,
        },
        {
          id: "businessEmail",
          engageLabel: "Email",
          required: true,
          dataType: "string",
          dataTypeLabel: "string",
          informAttribute: null,
        },
        {
          id: "businessCountry",
          engageLabel: "Country",
          required: true,
          dataType: "string",
          dataTypeLabel: "string",
          informAttribute: null,
        },
        {
          id: "userIdAtClient",
          engageLabel: "Employee ID",
          required: false,
          dataType: "string",
          dataTypeLabel: "string",
          informAttribute: null,
        },
        {
          id: "jobTitle",
          engageLabel: "Job Title",
          required: false,
          dataType: "string",
          dataTypeLabel: "string",
          informAttribute: null,
        },
        {
          id: "organization",
          engageLabel: "Organization",
          required: false,
          dataType: "string",
          dataTypeLabel: "string",
          informAttribute: null,
        },
        {
          id: "businessPhone",
          engageLabel: "Phone Number",
          required: false,
          dataType: "string",
          dataTypeLabel: "string",
          informAttribute: null,
        },
        {
          id: "jobStartDate",
          engageLabel: "Start Date",
          required: false,
          dataType: "date",
          dataTypeLabel: "date",
          informAttribute: null,
        },
        // Personal details. Mappable like any other property, which is what makes them
        // override-able — and, left unmapped, what leaves them editable in Whistle for
        // an Inform-managed user, since a feed that supplies nothing cannot win a
        // collision that never happens.
        {
          id: "displayName",
          engageLabel: "Display Name",
          required: false,
          dataType: "string",
          dataTypeLabel: "string",
          informAttribute: null,
        },
        {
          id: "birthday",
          engageLabel: "Birthday",
          required: false,
          dataType: "date",
          dataTypeLabel: "date",
          informAttribute: null,
        },
        {
          id: "personalEmail",
          engageLabel: "Personal Email",
          required: false,
          dataType: "string",
          dataTypeLabel: "string",
          informAttribute: null,
        },
        {
          id: "personalPhone",
          engageLabel: "Personal Phone",
          required: false,
          dataType: "string",
          dataTypeLabel: "string",
          informAttribute: null,
        },
      ],
    };
  },
  watch: {
    client: {
      immediate: true,
      async handler(newClient) {
        this.informTenantId = newClient?.informTenantId || "";
        this.debouncedPersistSyncFilter = debounce((value) => {
          this.syncFilterField.persist(this.client.clientId, value, this.supplementalServices);
        }, FILTER_DEBOUNCE_MS);
        this.debouncedPersistCleanupFilter = debounce((value) => {
          this.cleanupFilterField.persist(this.client.clientId, value, this.supplementalServices);
        }, FILTER_DEBOUNCE_MS);
        if (!newClient?.clientId) return;
        // Before anything renders: this decides which of the two flows the tab is in,
        // and it is evaluated against the SELECTED client, not the signed-in admin's.
        await this.loadClientFeatureFlags();
        if (!newClient?.informTenantId) return;
        await Promise.all([
          this.getSchemas(),
          this.schemaField.load(newClient.clientId, this.supplementalServices),
          this.loadInformUserMap(),
          this.syncFilterField.load(newClient.clientId, this.supplementalServices),
          this.cleanupFilterField.load(newClient.clientId, this.supplementalServices),
        ]);
        if (this.engageUserSyncEnabled) {
          await this.loadEngageConfiguration();
        }
      },
    },
    "schemaField.value"(newVal, oldVal) {
      if (this.schemaField.loading) return;
      if (this.isRevertingSelectedSchema) return;
      if (newVal === oldVal) return;
      if (!this.hasClientId) return;
      this.handleSelectedSchemaChange(newVal, oldVal);
    },
    "confirmDialog.show"(newVal) {
      // Backdrop click / escape closes the dialog without firing confirm or
      // cancel — treat that as a cancel so callers don't dangle.
      if (!newVal && this.confirmDialogResolve) {
        this.confirmDialogResolve(false);
        this.confirmDialogResolve = null;
      }
    },
    informUserMap(newVal, oldVal) {
      if (this.mapField.loading) return;
      if (this.engageUserSyncEnabled) return;
      if (!this.hasClientId) return;
      if (JSON.stringify(newVal) === JSON.stringify(oldVal)) return;
      this.mapField.persist(
        this.client.clientId,
        JSON.stringify(newVal),
        this.supplementalServices
      );
    },
    "syncFilterField.value"(newVal, oldVal) {
      if (this.syncFilterField.loading) return;
      if (this.engageUserSyncEnabled) return;
      if (!this.hasClientId) return;
      if (newVal === oldVal) return;
      this.debouncedPersistSyncFilter(newVal);
    },
    "cleanupFilterField.value"(newVal, oldVal) {
      if (this.cleanupFilterField.loading) return;
      if (this.engageUserSyncEnabled) return;
      if (!this.hasClientId) return;
      if (newVal === oldVal) return;
      this.debouncedPersistCleanupFilter(newVal);
    },
  },
  computed: {
    supplementalServices() {
      return {
        getClientSupplemental: this.getClientSupplemental,
        saveClientSupplemental: this.saveClientSupplemental,
        softDeleteClientSupplemental: this.softDeleteClientSupplemental,
        showSuccess: (payload) => this.$store.dispatch("showSuccess", payload),
        showError: (payload) => this.$store.dispatch("showError", payload),
        // eslint-disable-next-line vue/valid-next-tick
        deferFn: (fn) => this.$nextTick(fn),
      };
    },
    hasClientId() {
      return !!(this.client && this.client.clientId);
    },
    informTenantIdTrimmed() {
      return (this.informTenantId || "").trim();
    },
    savedInformTenantId() {
      return this.client?.informTenantId?.trim() || "";
    },
    hasSavedTenantId() {
      return !!this.savedInformTenantId;
    },
    hasUnsavedTenantIdChanges() {
      return this.informTenantId.trim() !== this.savedInformTenantId;
    },
    refreshDisabled() {
      return (
        !this.hasClientId || this.saving || !this.hasSavedTenantId || this.hasUnsavedTenantIdChanges
      );
    },
    schemaOptions() {
      return this.schemas.map((schema) => schema.name);
    },
    selectedSchemaFields() {
      if (!this.schemaField.value) return [];
      return this.schemas.find((schema) => schema.name === this.schemaField.value)?.fields || [];
    },
    informAttributeOptions() {
      return this.selectedSchemaFields.map((field) => field.name);
    },
    informUserMap() {
      return this.schemaMappingRows.reduce((acc, row) => {
        if (row.informAttribute) {
          acc[row.id] = row.informAttribute;
        }
        return acc;
      }, {});
    },
    /**
     * Which of the two flows this tab is in, for the client being configured.
     *
     * Off is today's tab, unchanged: GraphQL filter help, auto-saving watchers, no
     * override control and no save button. On moves the client to the merged-user flow.
     */
    engageUserSyncEnabled() {
      return this.isEngageUserSyncEnabled();
    },
    /**
     * Whether the flag set in the store belongs to the client this tab is showing.
     *
     * Not the same question as "is the flag on". On a full page load the parent's
     * `currentClient` falls back to the signed-in admin's own client until the clients
     * list arrives, so the first value this tab sees is the wrong client — and the flag
     * resolved for it is the wrong answer. Keying on the id makes that state visibly
     * unresolved instead of silently off.
     */
    clientFeatureFlagsReady() {
      return this.isClientFeatureFlagsResolvedFor(this.client?.clientId);
    },
    /**
     * The override control picks from the same Engage properties the mapping table
     * lists, so there is nothing extra for an admin to learn or mistype.
     */
    overrideOptions() {
      return this.schemaMappingRows.map((row) => ({ text: row.engageLabel, value: row.id }));
    },
    /**
     * Required only once a schema is chosen. An Engage-owned tenant (no schema) has no
     * feed, so its sync reads nothing and a filter there would mean nothing.
     *
     * Not judged while the stored filter is still loading: the schema can arrive first,
     * and the field is blank until its own request returns.
     */
    syncFilterError() {
      if (!this.engageUserSyncEnabled) return null;
      if (
        this.schemaField.value &&
        !this.syncFilterField.loading &&
        !(this.syncFilterField.value || "").trim()
      ) {
        return "User Sync Filter is required.";
      }
      return validateInformFilter(this.syncFilterField.value, "sync");
    },
    cleanupFilterError() {
      if (!this.engageUserSyncEnabled) return null;
      return validateInformFilter(this.cleanupFilterField.value, "cleanup");
    },
    /**
     * Required Engage fields with no Inform attribute chosen — which is only a question
     * when there is a schema to choose from.
     *
     * **A blank schema is a valid configuration, not an incomplete one.** It declares
     * that Engage owns this tenant's users outright: no client feed, no merge, one
     * `One10_User` node per person. `buildEngageConfiguration` already treats it that
     * way, nulling the external label, email property and rename map together. Holding
     * the mapping rows against it made the one configuration an Engage-owned client
     * needs the one this form could never save, because there is nothing to map them to.
     */
    unmappedRequiredFields() {
      if (!this.schemaField.value) return [];
      return this.schemaMappingRows.filter((row) => row.required && !row.informAttribute);
    },
    /**
     * A filter still loading is blank, and saving it would delete the stored one. The
     * button and the save handler both check this, so neither path can do that.
     */
    filtersLoading() {
      return this.syncFilterField.loading || this.cleanupFilterField.loading;
    },
    saveConfigurationDisabled() {
      return (
        !this.hasClientId ||
        this.savingConfiguration ||
        this.refreshing ||
        this.unmappedRequiredFields.length > 0 ||
        this.filtersLoading ||
        !!this.syncFilterError ||
        !!this.cleanupFilterError
      );
    },
    /**
     * The supplemental services with the per-field success snackbar suppressed.
     *
     * The button writes four supplementals in one action; four "saved" messages for one
     * click is noise. The handler shows a single message covering all of it.
     */
    silentSupplementalServices() {
      return { ...this.supplementalServices, showSuccess: () => {} };
    },
  },
  methods: {
    dataTypeToStatusVariant(dataType) {
      if (dataType === "number") return "success";
      if (dataType === "date") return "warning";
      if (dataType === "string") return "info";
      return "default";
    },
    isMockMappingRowInError(row) {
      if (!row.required || !this.mockSchemaShowValidation) return false;
      return !row.informAttribute;
    },
    clearSchemaMappingRows() {
      this.schemaMappingRows.forEach((row) => {
        row.informAttribute = null;
      });
    },
    /**
     * Whether `attribute` is currently mapped to a row OTHER than `currentRow`.
     * Drives the greyed-out styling in the per-row attribute dropdown so the
     * user can see at a glance which attributes are already in use elsewhere.
     */
    isAttributeUsedElsewhere(attribute, currentRow) {
      if (!attribute) return false;
      return this.schemaMappingRows.some(
        (row) => row.id !== currentRow.id && row.informAttribute === attribute
      );
    },
    /**
     * Each Inform attribute can only map to a single Engage user property.
     * When the user picks an attribute that's already mapped to another row,
     * clear that other row so the new row takes ownership exclusively. The
     * `informUserMap` watcher batches both changes into a single persist.
     */
    handleInformAttributeChange(row, newValue) {
      if (!newValue) return;
      const previousOwner = this.schemaMappingRows.find(
        (other) => other.id !== row.id && other.informAttribute === newValue
      );
      if (previousOwner) {
        previousOwner.informAttribute = null;
      }
    },
    /**
     * Open the shared confirmation dialog.
     *
     * @param {Object} options
     * @param {string} options.title
     * @param {string} options.message
     * @param {string} [options.confirmButtonText]
     * @param {string} [options.cancelButtonText]
     * @returns {Promise<boolean>} resolves true when confirmed, false when
     *   cancelled or dismissed.
     */
    confirm({ title, message, confirmButtonText = "Confirm", cancelButtonText = "Cancel" }) {
      this.confirmDialog = {
        show: true,
        title,
        message,
        confirmButtonText,
        cancelButtonText,
      };
      return new Promise((resolve) => {
        this.confirmDialogResolve = resolve;
      });
    },
    onConfirmDialogConfirm() {
      if (this.confirmDialogResolve) {
        this.confirmDialogResolve(true);
        this.confirmDialogResolve = null;
      }
    },
    onConfirmDialogCancel() {
      if (this.confirmDialogResolve) {
        this.confirmDialogResolve(false);
        this.confirmDialogResolve = null;
      }
    },
    /**
     * Public — called by parents (DeveloperSettings, AccountManagementV2) to
     * gate navigation away from this view. Resolves to:
     *   - true when no schema is selected yet (setup hasn't started),
     *   - true when every required row has an attribute mapped,
     *   - the user's choice when there are unmapped required fields.
     */
    async confirmCanLeave() {
      if (!this.schemaField.value) return true;
      const unmapped = this.schemaMappingRows.filter((row) => row.required && !row.informAttribute);
      if (unmapped.length === 0) return true;
      const fieldList = unmapped.map((row) => row.engageLabel).join(", ");
      return this.confirm({
        title: "Required fields not mapped",
        message:
          `The following required fields are still unmapped: ${fieldList}. ` +
          "Inform won't sync users until they're filled in. Leave anyway?",
        confirmButtonText: "Leave anyway",
        cancelButtonText: "Continue mapping",
      });
    },
    async handleSelectedSchemaChange(newVal, oldVal) {
      if (oldVal) {
        const confirmed = await this.confirm({
          title: "Change schema?",
          message:
            "Changing the schema will clear the current attribute mapping. This action cannot be undone.",
          confirmButtonText: "Change schema",
        });
        if (!confirmed) {
          this.isRevertingSelectedSchema = true;
          this.schemaField.value = oldVal;
          this.$nextTick(() => {
            this.isRevertingSelectedSchema = false;
          });
          return;
        }
      }
      this.clearSchemaMappingRows();
      if (!newVal) return;
      if (this.engageUserSyncEnabled) return;
      this.schemaField.persist(this.client.clientId, newVal, this.supplementalServices);
    },
    mockMappingErrorMessages(row) {
      return this.isMockMappingRowInError(row) ? [`${row.engageLabel} is required`] : [];
    },
    validateClientSelected() {
      if (!this.client?.clientId) {
        this.$store.dispatch("showError", {
          message: "No client selected.",
        });
        return false;
      }
      return true;
    },

    validateTenantId() {
      if (!this.validateClientSelected()) return false;
      const tenantId = this.informTenantId?.trim();
      if (!tenantId) {
        this.$store.dispatch("showError", {
          message: "Inform tenant id is required.",
        });
        return false;
      }
      return true;
    },
    async saveTenantId(showSuccessMessage = true) {
      if (!this.validateClientSelected()) return null;

      const tenantId = this.informTenantId.trim();

      await this.clientService.updateClientV3(this.client.clientId, {
        informTenantId: tenantId || null,
      });

      this.$emit("getClient", { clientId: this.client.clientId });
      // Readers of the store's clients list (e.g. Program Admin's builder card) see the new tenant.
      this.$store.dispatch("setClients");
      if (showSuccessMessage) {
        this.$store.dispatch("showSuccess", {
          message: tenantId ? "Changes saved successfully." : "Inform tenant ID removed.",
        });
      }

      return tenantId;
    },
    async handleSaveTenantId() {
      const isClearing = !this.informTenantIdTrimmed && this.hasSavedTenantId;

      if (isClearing) {
        const confirmed = await this.confirm({
          title: "Remove Inform tenant ID?",
          message:
            "Removing the tenant ID will disconnect this client from Inform and clear the selected schema and current attribute mapping. This action cannot be undone.",
          confirmButtonText: "Remove tenant ID",
        });
        if (!confirmed) return;
      } else if (this.hasUnsavedTenantIdChanges && this.hasSavedTenantId) {
        // Confirm when the tenant id changes to a different value — the change
        // wipes the schema/mapping.
        const confirmed = await this.confirm({
          title: "Update Inform tenant ID?",
          message:
            "Updating the tenant ID will clear the selected schema and current attribute mapping. This action cannot be undone.",
          confirmButtonText: "Update tenant ID",
        });
        if (!confirmed) return;
      }
      try {
        this.saving = true;
        const previousTenantId = this.savedInformTenantId;
        const savedTenantId = await this.saveTenantId(true);
        if (savedTenantId !== null && savedTenantId !== previousTenantId) {
          await this.resetSchemaAndMapping();
          if (savedTenantId) {
            await this.getSchemas();
          } else {
            this.schemas = [];
          }
        }
      } catch (error) {
        console.error("Failed to save Inform tenant id", error);
        this.$store.dispatch("showError", {
          message: "Failed to save Inform tenant id. Please try again later.",
        });
      } finally {
        this.saving = false;
      }
    },
    /**
     * Clears the selected schema + schema-mapping UI state and persists empty
     * values for both client-supplemental keys. Bypasses the auto-save
     * watchers (and their per-action snackbars) by setting the loading flags
     * and calling `saveClientSupplemental` directly.
     */
    async resetSchemaAndMapping() {
      if (!this.hasClientId) return;
      this.schemaField.loading = true;
      this.mapField.loading = true;
      this.syncFilterField.loading = true;
      this.cleanupFilterField.loading = true;
      try {
        this.clearSchemaMappingRows();
        await Promise.all([
          this.schemaField.reset(this.client.clientId, this.supplementalServices),
          this.mapField.reset(this.client.clientId, this.supplementalServices),
          this.syncFilterField.reset(this.client.clientId, this.supplementalServices),
          this.cleanupFilterField.reset(this.client.clientId, this.supplementalServices),
        ]);
      } catch (error) {
        console.error("Failed to reset Inform schema mapping", error);
        this.$store.dispatch("showError", {
          message: "Failed to reset Inform schema mapping. Please try again later.",
        });
      } finally {
        this.$nextTick(() => {
          this.schemaField.loading = false;
          this.mapField.loading = false;
          this.syncFilterField.loading = false;
          this.cleanupFilterField.loading = false;
        });
      }
    },
    async getSchemas() {
      try {
        const response = await this.informService.getSchema({
          clientId: this.client.clientId,
        });
        this.schemas = response.result || [];
      } catch (error) {
        console.error("Failed to get Inform schemas", error);
        this.schemas = [];
        this.$store.dispatch("showError", {
          message: "Failed to get Inform schemas. Please try again later.",
        });
      }
    },

    async loadInformUserMap() {
      if (!this.hasClientId) return;
      try {
        this.mapField.loading = true;
        const { value, clientSupplementalId } = await this.getClientSupplemental({
          clientId: this.client.clientId,
          key: INFORM_USER_MAP_KEY,
          errorContext: "Failed to load saved Inform user map",
        });
        this.mapField.supplementalId = clientSupplementalId;
        let parsed = {};
        if (value) {
          try {
            parsed = JSON.parse(value) || {};
          } catch (parseError) {
            console.error("Failed to parse saved Inform user map", parseError);
            parsed = {};
          }
        }
        this.schemaMappingRows.forEach((row) => {
          row.informAttribute = parsed[row.id] || null;
        });
      } catch (error) {
        console.error("Failed to load Inform user map", error);
        this.$store.dispatch("showError", {
          message: "Failed to load saved Inform user map. Please try again later.",
        });
      } finally {
        this.$nextTick(() => {
          this.mapField.loading = false;
        });
      }
    },

    async handleRefreshSchemas() {
      if (!this.validateClientSelected()) return;

      const confirmed = await this.confirm({
        title: "Refresh Inform schemas?",
        message:
          "Refreshing schemas will pull the latest schemas from Inform and clear the selected schema and current attribute mapping. This action cannot be undone.",
        confirmButtonText: "Refresh schemas",
      });
      if (!confirmed) return;

      try {
        this.refreshing = true;
        await this.informService.refreshSchemas({
          clientId: this.client.clientId,
        });
        await this.resetSchemaAndMapping();
        await this.getSchemas();
        this.$store.dispatch("showSuccess", {
          message: "Inform schemas refresh successful.",
        });
      } catch (error) {
        console.error("Failed to refresh Inform schemas", error);
        this.$store.dispatch("showError", {
          message: "Failed to refresh Inform schemas. Please try again later.",
        });
      } finally {
        this.refreshing = false;
      }
    },

    /**
     * Read the tenant's Engage merge configuration and take the override list off it.
     *
     * The override list lives in the graph configuration rather than in a
     * client-supplemental row of its own: it is tenant configuration, and keeping one
     * copy means the control cannot disagree with what the merge actually does.
     *
     * An unconfigured tenant is a normal steady state — the endpoint returns 200 with
     * an empty document rather than a 404 — so nothing here treats "no configuration
     * yet" as a failure.
     */
    async loadEngageConfiguration() {
      if (!this.hasClientId) return;
      try {
        const configuration = await this.informService.getEngageConfiguration({
          clientId: this.client.clientId,
        });
        this.allowOverrideProperties = configuration?.AllowUserOverrideProperties || [];
      } catch (error) {
        console.error("Failed to load Engage configuration", error);
        this.allowOverrideProperties = [];
        this.$store.dispatch("showError", {
          message: "Failed to load the Engage merge configuration. Please try again later.",
        });
      }
    },

    /**
     * Poll until the configuration read back carries the fields just written.
     *
     * The POST returns 202 and the consumer applies it off a Kafka topic, so the first
     * read after a save returns the previous document. Only the Engage-owned fields are
     * compared: another team writing a metric or payout label in the same window is a
     * legitimate concurrent change, not a lost write.
     *
     * @param {number|string} clientId
     * @param {Object} expected The document that was posted.
     * @returns {Promise<boolean>} false when it never appeared within the budget.
     */
    async confirmConfigurationWritten(clientId, expected) {
      for (let attempt = 0; attempt < this.configurationConfirmAttempts; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, this.configurationConfirmDelayMs));
        const stored = await this.informService.getEngageConfiguration({ clientId });
        if (engageConfigurationMatches(stored, expected)) return true;
      }
      return false;
    },

    /**
     * Save everything the tab owns, in one action.
     *
     * Only reachable with the flag on. The watchers that auto-save each field stand
     * down in that flow, because a configuration write re-merges the whole tenant
     * upstream — nine dropdown picks would otherwise cost nine full re-merges.
     *
     * Order matters. The client-supplemental rows are written first because the nightly
     * sync reads its filters from them; the graph configuration goes second because it
     * is the expensive half and the one that can be refused for an illegal identifier.
     *
     * The configuration is read immediately before it is written: the endpoint replaces
     * the stored document wholesale, so anything this tab does not set — the metric and
     * payout labels — would be cleared by a partial body.
     */
    async handleSaveInformConfiguration() {
      if (!this.validateClientSelected()) return;

      if (this.unmappedRequiredFields.length > 0) {
        const fieldList = this.unmappedRequiredFields.map((row) => row.engageLabel).join(", ");
        this.$store.dispatch("showError", {
          message: `Map the required fields before saving: ${fieldList}.`,
        });
        return;
      }
      if (this.filtersLoading) {
        this.$store.dispatch("showError", {
          message: "The filters are still loading. Try again in a moment.",
        });
        return;
      }
      const filterError = this.syncFilterError || this.cleanupFilterError;
      if (filterError) {
        this.$store.dispatch("showError", { message: filterError });
        return;
      }

      const clientId = this.client.clientId;
      try {
        this.savingConfiguration = true;

        await Promise.all([
          this.schemaField.persist(
            clientId,
            this.schemaField.value,
            this.silentSupplementalServices
          ),
          this.mapField.persist(
            clientId,
            JSON.stringify(this.informUserMap),
            this.silentSupplementalServices
          ),
          this.syncFilterField.persist(
            clientId,
            this.syncFilterField.value,
            this.silentSupplementalServices
          ),
          this.cleanupFilterField.persist(
            clientId,
            this.cleanupFilterField.value,
            this.silentSupplementalServices
          ),
        ]);

        const current = await this.informService.getEngageConfiguration({ clientId });
        const configuration = buildEngageConfiguration({
          current: current || {},
          externalUserLabel: this.schemaField.value,
          informUserMap: this.informUserMap,
          allowOverrideProperties: this.allowOverrideProperties,
        });
        await this.informService.saveEngageConfiguration({ clientId, configuration });

        // Wait for the write to appear before releasing the button. Saving twice inside
        // this window is the hazard: the second save reads the pre-first document, and
        // because the endpoint replaces wholesale it would carry a concurrent writer's
        // metric or payout label back to its previous value.
        const confirmed = await this.confirmConfigurationWritten(clientId, configuration);
        if (!confirmed) {
          // Visible, not a silent retry. The write may well land a moment later, but an
          // admin who saves again on a stale read is the thing this is here to prevent.
          this.$store.dispatch("showError", {
            message:
              "Inform configuration was accepted but has not appeared yet. Reload this tab before saving again.",
          });
          return;
        }

        this.$store.dispatch("showSuccess", {
          message: "Inform configuration saved.",
        });
      } catch (error) {
        console.error("Failed to save Inform configuration", error);
        // Two failures here carry a reason worth showing, and the shared axios handler
        // surfaces neither. A rejected identifier is a 400 whose body is a bare string,
        // which that handler skips because it looks for `message`/`error` keys. A 403 is
        // the likelier one in practice: this tab renders on `inform:manage:schema` but
        // the proxy requires `inform:manage:user-sync` to write, so an admin can fill the
        // whole form in and still be refused at the last step.
        const body = error?.response?.data;
        const reason = typeof body === "string" ? body : body?.message || null;
        this.$store.dispatch("showError", {
          message: reason
            ? `Failed to save Inform configuration: ${reason}`
            : "Failed to save Inform configuration. Please try again later.",
        });
      } finally {
        this.savingConfiguration = false;
      }
    },
  },
};
</script>

<style scoped>
/*
 * `dense` sizes a select for one line of text, which leaves a chip stack no vertical
 * room — the chips press into the fieldset legend and each other. Kept dense so the
 * control still matches the rest of the form, with the room given back here.
 */
.override-select >>> .v-select__selections {
  padding: 6px 0;
  min-height: 44px;
}

.override-select >>> .v-select__selections .v-chip {
  margin: 4px 6px 4px 0;
}

.schema-mapping-table {
  width: 100%;
}

.schema-mapping-header {
  min-height: 56px;
  padding: 0 16px;
  background: #f6f6f6;
}

.schema-mapping-row {
  min-height: 56px;
  padding: 0 16px;
}

.schema-mapping-col-engage {
  flex: 1 1 200px;
  min-width: 0;
}

.schema-mapping-col-inform {
  flex: 1 1 280px;
  min-width: 200px;
}

.schema-mapping-grid-line {
  border-color: rgba(0, 0, 0, 0.12) !important;
}

.graphql-filter-field {
  max-width: 500px;
  width: 500px;
  min-width: 250px;
}

.graphql-filter-field >>> .v-input__control > .v-input__slot {
  min-height: 125px;
  align-items: flex-start;
}

.inform-neo4j-link {
  font-size: 16px;
  text-decoration: underline;
}

.inform-filter-example {
  background: #f6f6f6;
  border-radius: 4px;
  padding: 8px 12px;
  margin: 8px 0 0;
  max-width: 500px;
  overflow-x: auto;
  font-size: 12px;
  line-height: 1.5;
}
</style>
